import ArrowBack from '@mui/icons-material/ArrowBack'
import HelpOutline from '@mui/icons-material/HelpOutline'
import OpenInNew from '@mui/icons-material/OpenInNew'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Skeleton from '@mui/material/Skeleton'
import { usePreferences } from '../hooks'

type BannerActionsProps = {
  envId: string | undefined
  apiKey: string | undefined
  isLoading: boolean
  layout?: 'default' | 'compact'
}

export function BannerActions({ envId, apiKey, isLoading: parentLoading, layout = 'default' }: BannerActionsProps) {
  const { data, isLoading } = usePreferences(envId ?? '', apiKey ?? '')

  const loading = parentLoading || isLoading

  if (loading) {
    return (
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        sx={
          layout === 'compact'
            ? { flexDirection: 'column', alignItems: 'center', flexWrap: 'nowrap' }
            : undefined
        }
      >
        {layout === 'compact' ? (
          <>
            <Skeleton width={160} height={20} variant="text" />
            <Skeleton width={220} height={44} variant="rectangular" sx={{ borderRadius: 2 }} />
          </>
        ) : (
          <>
            <Skeleton width={120} height={40} variant="rectangular" />
            <Skeleton width={120} height={40} variant="rectangular" />
          </>
        )}
      </Box>
    )
  }

  if (!data?.connectMainLink && !data?.connectHelpLink) return null

  if (layout === 'compact') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={3}
        width="100%"
        mt={2}
      >
        {data.connectHelpLink && (
          <Link
            href={data.connectHelpLink}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            color="text.light"
            sx={{ fontSize: '0.875rem', textAlign: 'center' }}
          >
            {data.connectHelpLinkText ?? 'View help instructions'}
          </Link>
        )}
        {data.connectMainLink && (
          <Button
            component="a"
            href={data.connectMainLink}
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 2,
              bgcolor: 'grey.100',
              borderColor: 'divider',
              color: 'text.lightish',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'grey.200',
                borderColor: 'divider',
              },
            }}
          >
            {data.connectMainLinkText ?? 'Visit Main Website'}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box display="flex" sx={{ justifyContent: 'start', flexWrap: 'wrap', gap: 2 }}>
      {data.connectMainLink && (
        <Button
          component="a"
          href={data.connectMainLink}
          target="_blank"
          variant="contained"
          color="primary"
          sx={{ px: 3, py: 1.5 }}
          endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
        >
          {data.connectMainLinkText ?? 'Visit Main Website'}
        </Button>
      )}
      {data.connectHelpLink && (
        <Button
          component="a"
          href={data.connectHelpLink}
          target="_blank"
          variant="outlined"
          color="secondary"
          sx={{ px: 3, py: 1.5 }}
          endIcon={<HelpOutline sx={{ fontSize: 16 }} />}
        >
          {data.connectHelpLinkText ?? 'Help Instructions'}
        </Button>
      )}
    </Box>
  )
}
